import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Cluster,
  Cover,
  Divider,
  Expand,
  FormItem,
  Heading,
  Icon,
  ImageWrapper,
  ISelectOption,
  Label,
  Notification,
  Paragraph,
  Select,
  Sidebar,
  Spinner,
  Stack,
  Status,
  Table,
  TdLink,
  Template,
  Toast,
  useValidateForm,
} from '@pluto-tv/assemble';
import channelRoutes from 'routes/programming.routes';
import {IChannel} from 'models/channels';
import useToggleSearchBarOnSlash from 'helpers/useToggleSearchBarOnSlash';
import {useAppPermissions} from 'app/permissions';
import {withThousandsSeparator} from 'utils/thousands-separator';
import {orderBy} from 'lodash-es';
import {useFindQuery as useFindDevicesQuery} from 'features/devices/devicesApi';
import {useUserRegions} from 'helpers/useUserRegions';
import {INestedMainCategoriesProps} from 'views/programming/mainCategories/nestedPropsInterface';
import useSearchChannels, {INIT_PARAMS} from 'views/programming/mainCategories/hooks/useGetSearchChannels';
import {
  IMainCategoryChannelsRequest,
  IMainCategoryChannelsSearch,
  IMainCategoryChannelsSearchForm,
} from 'models/mainCategories';
import {channelListSearchValidator} from 'views/programming/mainCategories/validators';
import {rewriteImageUrl} from 'utils/imageUtils';
import {useUpdateChannelsMutation} from 'features/mainCategories/mainCategoriesApi';
import {getFilteredDeviceExcludedList, getFilteredDeviceIncludedList} from 'utils/deviceUtils';

const ReorderChannelsList = React.lazy(() => import('./ReorderChannelsList'));

export interface IChannelListProps extends INestedMainCategoriesProps {
  actionsCol?: boolean;
  addNewChannel?: boolean;
  deviceUpdates?: boolean;
  checkboxCol?: boolean | 'multiple';
  filterByArchived?: boolean;
  showFavoriteSearch?: boolean;
  inModal?: boolean;
  onSelect?: (rows: IChannel[]) => void;
  onRowClick?: (row: IChannel) => void;
  nameTarget?: React.HTMLAttributeAnchorTarget;
  isSearchExpanded?: boolean;
  searchRegion?: string;
  disableChannels?: IChannel[] | Partial<IChannel>[];
  hideArchivedChannels?: boolean;
  isDirty?: boolean;
}

const KIDS_MODE_OPTIONS: ISelectOption[] = [
  {label: 'Yes', value: true},
  {label: 'No', value: false},
];

const OFFICE_MODE_OPTIONS: ISelectOption[] = [
  {label: 'Yes', value: true},
  {label: 'No', value: false},
];

export default React.memo(({isSearchExpanded = true, model, isDirty}: IChannelListProps): JSX.Element => {
  const [searchExpanded, setSearchExpanded] = React.useState(isSearchExpanded);
  const [reorderOpen, setReorderOpen] = React.useState(false);
  const [channelList, setChannelList] = React.useState<IChannel[]>([]);
  const [updateChannels] = useUpdateChannelsMutation();

  const {ableTo} = useAppPermissions();
  const {territories} = useUserRegions();

  const getChannelEditUrl = (channel: IChannel) =>
    channelRoutes.paths.channelEditDetailsPage.replace(':id', channel?.id);

  useToggleSearchBarOnSlash(setSearchExpanded, searchExpanded);

  const {data: deviceTypesList} = useFindDevicesQuery({
    offset: 0,
    limit: 250,
    sort: 'name:asc',
  });

  const {search: onSearch, data: channels, totalCount, isError, isLoading, reset} = useSearchChannels();
  const {
    model: searchModel,
    setFields: setSearchFields,
    form: searchForm,
    reset: searchReset,
    setModel: setSearchModel,
  } = useValidateForm<IMainCategoryChannelsSearchForm>(channelListSearchValidator, 'ask');

  React.useEffect(() => {
    if (!model.id) return;

    onSearch({id: model.id, order: model.stackingOrder || 'natural', ...INIT_PARAMS});
    setSearchModel({published: true});
  }, [onSearch, model.id, model.stackingOrder, setSearchModel]);

  const filteredDeviceIncludedList = React.useMemo(() => {
    return getFilteredDeviceIncludedList(deviceTypesList?.data || [], searchModel.devicesExcluded || []);
  }, [deviceTypesList?.data, searchModel.devicesExcluded]);

  const filteredDeviceExcludedList = React.useMemo(() => {
    return getFilteredDeviceExcludedList(deviceTypesList?.data || [], searchModel.devicesIncluded || []);
  }, [deviceTypesList?.data, searchModel.devicesIncluded]);

  React.useEffect(() => {
    if (reorderOpen || isLoading || isDirty) return;
    setChannelList(channels || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, isLoading]);

  const openReorder = () => {
    setReorderOpen(true);
  };

  const handleSearchClear = React.useCallback(() => {
    searchReset();
    reset();
    setSearchModel({published: true});
  }, [reset, searchReset, setSearchModel]);

  const handleSearch = React.useCallback(() => {
    const params: IMainCategoryChannelsSearch = {
      id: model.id || '',
      ...INIT_PARAMS,
      ...searchModel,
      order: model.stackingOrder || 'natural',
    };
    delete params.published;
    delete params.unpublished;
    delete params.publishedState;

    if (searchModel.published && !searchModel.unpublished) {
      params.publishedState = true;
    } else if (searchModel.unpublished && !searchModel.published) {
      params.publishedState = false;
    }

    onSearch(params);
  }, [model.id, model.stackingOrder, onSearch, searchModel]);

  const handleReorderSave = React.useCallback(
    async (rows: IChannel[], order: 'natural' | 'custom') => {
      const channelsUpdated = channelList
        .map(channel => {
          const newChannel = rows.find(row => row.id === channel.id);
          return newChannel ?? {...channel, index: channelList.length + channel.number};
        })
        .sort((a: any, b: any) => a.index - b.index || a.number - b.number);

      setChannelList(channelsUpdated);

      let bodyReq: IMainCategoryChannelsRequest = {
        mainCategoryId: model?.id || '',
        order: order || 'natural',
      };

      bodyReq = {...bodyReq, channelIds: rows.map(row => row.id) || []};

      const result: any = await updateChannels(bodyReq);
      if ('error' in result) Toast.error('error', `Falied to update channels. Please try again.`);
      else Toast.success('Success', `Successfully updated channels.`);

      setReorderOpen(false);
    },
    [channelList, model.id, updateChannels],
  );

  return (
    <>
      <Sidebar fullHeight={true}>
        <Expand width='21rem' height='100%' fullHeightContainer={true} isExpanded={searchExpanded}>
          <Template label='expandable'>
            <Box
              background='pewter'
              paddingY={'medium'}
              paddingRight='medium'
              paddingLeft={'medium'}
              fullHeight={true}
              marginRight='large'
              borderTop={true}
              borderSize='0.125rem'
              marginTop='medium'
              borderColor='cavern'
            >
              <Cover scrolling={true} gutter='medium'>
                <Template label='header'>
                  <Stack space='medium'>
                    <Cluster align='center' justify='space-between'>
                      <Icon icon='tune' space='small' size='large' iconAlign='center'>
                        <Heading level='h4'>Search Filters</Heading>
                      </Icon>
                      <Icon
                        id='closeFilters'
                        icon='collapseleft'
                        size='large'
                        onClick={() => setSearchExpanded(!searchExpanded)}
                      />
                    </Cluster>
                    <Divider color='graphite' />
                  </Stack>
                </Template>
                <Template label='cover'>
                  {searchExpanded && (
                    <form
                      id='searchFilter'
                      onSubmit={ev => {
                        ev.preventDefault();
                        handleSearch();
                      }}
                    >
                      <Stack space='small'>
                        {/* Using this to allow pressing enter to submit form */}
                        <input type='submit' style={{display: 'none'}} />
                        <Stack space='xxlarge'>
                          <Stack space='small'>
                            <Label>Status</Label>
                            <Stack space='small'>
                              <Checkbox
                                id='publishedFilter'
                                label='Published'
                                value={searchModel.published}
                                onChange={val => {
                                  setSearchFields({published: val});
                                }}
                              />
                              <Checkbox
                                id='unpublishedFilter'
                                label='Unpublished'
                                value={searchModel.unpublished}
                                onChange={val => {
                                  setSearchFields({unpublished: val});
                                }}
                              />
                            </Stack>
                          </Stack>
                          <FormItem label='Territories Included'>
                            <Select
                              placeholder='Select Territory'
                              id='territoriesIncluded'
                              value={searchModel.territoriesIncluded?.map(d => ({label: d, value: d}))}
                              multiselect={true}
                              clearable={true}
                              searchable={true}
                              appendToBody={true}
                              options={(territories || []).map(t => ({label: t.name, value: t.id}))}
                              onChange={value =>
                                setSearchFields({
                                  territoriesIncluded: (value || []).map(v => v.value),
                                })
                              }
                              predicate='value'
                            ></Select>
                          </FormItem>
                        </Stack>
                        <FormItem {...searchForm.devicesIncluded}>
                          <Select
                            id='devicesIncluded'
                            clearable={true}
                            placeholder='Select Devices'
                            multiselect={true}
                            appendToBody={true}
                            value={searchModel.devicesIncluded?.map(p => ({label: p, value: p}))}
                            options={filteredDeviceIncludedList}
                            onChange={value => {
                              setSearchFields({
                                devicesIncluded: ((value as ISelectOption[]) || []).map(v => v.value),
                              });
                            }}
                            predicate='value'
                            searchable={true}
                            onSearch={term =>
                              orderBy(
                                (filteredDeviceIncludedList || [])
                                  .filter(p => p.label.toLowerCase().includes(term.toLowerCase()))
                                  .map(p => ({label: p.label, value: p.value}), 'label'),
                              ) || ([] as ISelectOption[])
                            }
                          />
                        </FormItem>
                        <FormItem {...searchForm.devicesExcluded}>
                          <Select
                            id='devicesExcluded'
                            clearable={true}
                            placeholder='Select Devices'
                            appendToBody={true}
                            multiselect={true}
                            value={searchModel.devicesExcluded?.map(p => ({label: p, value: p}))}
                            options={filteredDeviceExcludedList}
                            onChange={value => {
                              setSearchFields({
                                devicesExcluded: ((value as ISelectOption[]) || []).map(v => v.value),
                              });
                            }}
                            predicate='value'
                            searchable={true}
                            onSearch={term =>
                              orderBy(
                                (filteredDeviceExcludedList || [])
                                  .filter(p => p.label.toLowerCase().includes(term.toLowerCase()))
                                  .map(p => ({label: p.label, value: p.value}), 'label'),
                              ) || ([] as ISelectOption[])
                            }
                          />
                        </FormItem>
                        <FormItem {...searchForm.kidsMode} label='Kids'>
                          <Select
                            id='kidsOnly'
                            clearable={true}
                            predicate='value'
                            placeholder='Select Kids Only'
                            options={KIDS_MODE_OPTIONS}
                            value={{label: '', value: searchModel.kidsMode}}
                            onChange={value => {
                              setSearchFields({kidsMode: value?.value});
                            }}
                          />
                        </FormItem>
                        <FormItem {...searchForm.plutoOfficeOnly} label='Office Only'>
                          <Select
                            id='officeOnly'
                            clearable={true}
                            predicate='value'
                            placeholder='Select Office Only'
                            options={OFFICE_MODE_OPTIONS}
                            value={{label: '', value: searchModel.plutoOfficeOnly}}
                            onChange={value => {
                              setSearchFields({plutoOfficeOnly: value?.value});
                            }}
                          />
                        </FormItem>
                      </Stack>
                    </form>
                  )}
                </Template>
                <Template label='footer'>
                  <Cluster justify='space-between'>
                    <div></div>
                    <Cluster space='small'>
                      <Button id='clearButton' ghost={true} onClick={handleSearchClear}>
                        Clear
                      </Button>
                      <Button id='searchButton' type='primary' onClick={handleSearch}>
                        Search
                      </Button>
                    </Cluster>
                  </Cluster>
                </Template>
              </Cover>
            </Box>
          </Template>
        </Expand>
        <Cover scrolling={true} gutterBottom='large' coverTemplateHeight='100%' overflow='auto' paddingY={'medium'}>
          <Template label='header'>
            <Box
              id='channelListHeader'
              background='pewter'
              borderTop={true}
              borderSize='0.125rem'
              borderColor='cavern'
              paddingTop={'small'}
              paddingBottom='small'
              paddingX={'large'}
            >
              <Cluster justify='space-between' align='center' space='medium'>
                <Cluster align='end' space='small'>
                  <Heading level='h2' color='secondary'>
                    Channels
                  </Heading>
                  <Cluster space='small' align='center'>
                    <Icon
                      id='expandFilter'
                      icon='tune'
                      space='xxxsmall'
                      verticalAlign='bottom'
                      lineHeight='0px'
                      onClick={() => setSearchExpanded(!searchExpanded)}
                    >
                      {withThousandsSeparator(totalCount || 0)} Items
                    </Icon>
                  </Cluster>
                </Cluster>
                <Cluster space='small' align='center'>
                  <Button
                    id='reorderBtn'
                    type='secondary'
                    onClick={openReorder}
                    state={channelList.filter(c => c.published).length === 0 ? 'disabled' : ''}
                    permission={ableTo('MAIN_CATEGORIES_EDIT') ? '' : 'disabled'}
                  >
                    Reorder
                  </Button>
                </Cluster>
              </Cluster>
            </Box>
          </Template>

          <Template label='cover'>
            <Table<IChannel>
              id='channelList'
              loading={isLoading}
              fixedHeader={true}
              predicate='id'
              wrapContent={true}
              emptyMsg={isError || (!channelList?.length && !isLoading) ? 'No channels associated.' : undefined}
              rows={channelList || []}
              cols={[
                {
                  label: 'Channel Name',
                  colMinWidth: '22.125rem',
                  transform: row => (
                    <Cluster wrap={false} space='small' align='center'>
                      <ImageWrapper
                        width='3rem'
                        height='3rem'
                        src={rewriteImageUrl(row.featuredImage?.path)}
                        alt={row.name}
                      />
                      <TdLink row={row} title={row.name || ''} url={getChannelEditUrl(row)} target='_blank' />
                    </Cluster>
                  ),
                },
                {
                  label: 'No.',
                  field: 'number',
                  colMinWidth: '5rem',
                },
                {
                  label: 'Published',
                  colMinWidth: '9.5rem',
                  transform: row => (
                    <Status
                      label={row.published ? 'Published' : 'Unpublished'}
                      state={row.published ? 'success' : 'neutral'}
                    />
                  ),
                },
                {
                  label: 'Kids',
                  colMinWidth: '8.875rem',
                  transform: row => (
                    <Status label={row.kidsMode ? 'Yes' : 'No'} state={row.kidsMode ? 'success' : 'neutral'} />
                  ),
                },
                {
                  label: 'Office Only',
                  colMinWidth: '8.875rem',
                  transform: row => (
                    <Status
                      label={row.plutoOfficeOnly ? 'Yes' : 'No'}
                      state={row.plutoOfficeOnly ? 'success' : 'neutral'}
                    />
                  ),
                },
              ]}
            >
              <Template label='loading'>
                <Cluster space='small' align='center'>
                  <Spinner />
                  <Paragraph>Loading Channel List</Paragraph>
                </Cluster>
              </Template>
              <Template label='empty'>
                <Notification type='warning'>There are no channels currently available.</Notification>
              </Template>
            </Table>
          </Template>
          <Template label='footer'>
            <></>
          </Template>
        </Cover>
      </Sidebar>
      {reorderOpen && (
        <React.Suspense fallback={<React.Fragment />}>
          <ReorderChannelsList
            reorderOpen={reorderOpen}
            setReorderOpen={setReorderOpen}
            handleReorderSave={handleReorderSave}
            getChannelEditUrl={getChannelEditUrl}
            model={model}
          />
        </React.Suspense>
      )}
    </>
  );
});
